import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Col, FormGroup, Form, Row, Button, Alert } from "react-bootstrap";
import { formikArrayInput, formikInput } from "../../../utils/utils";
import {
  Invoice2,
  InvoiceApprovalStatus,
  LineItem2,
  TransactionLine,
} from "src/docuclipper/DocuclipperTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  LoadingButton,
  TrackedButtonWithTooltip,
} from "src/views/Docuclipper/TrackedButton";
import {
  handleImportToQbo,
  setDuplicateError,
  updateInvoiceField,
  updateInvoiceLineItem,
} from "src/redux/reducers/InvoiceManager";
import { ReduxState } from "src/redux";
import {
  createAndAddQboAccount,
  fetchQboAccounts,
} from "src/redux/reducers/QboAccount";
import {
  createAndAddQboCustomer,
  fetchQboCustomers,
} from "src/redux/reducers/QboCustomer";
import {
  createAndAddQboVendor,
  fetchQboVendors,
} from "src/redux/reducers/QboVendor";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import {
  createAndAddQboAccountCategory,
  fetchQboAccountCategorys,
} from "src/redux/reducers/QboAccountCategory";
import { fetchQboItems } from "src/redux/reducers/QboItem";
import { FormikQboSelect } from "./QboSelect";
import CategorizationBadge from "./CategorizationBadge";
import TagRegexesInvoice from "../Analyze/Categorize/TagRegexesInvoice";
import { fetchQboTaxCodes } from "src/redux/reducers/QboTaxCode";
import { createAlert } from "src/redux/reducers/Alerts";
import { SUPPORTED_SCOPES } from "src/redux/reducers/TagLevelIntegration";
import {
  searchQboCustomers,
  searchQboVendors,
  searchQboAccounts,
  searchQboItems,
  searchQboTaxCodes,
  getTagLevelSetting,
  upsertTagLevelSetting,
} from "src/docuclipper/api";
import { ApprovalHistory } from "./ApprovalHistory";
import {
  fetchApprovalStatus,
  approveInvoice,
  rejectInvoice,
  resetState,
} from "src/redux/reducers/InvoiceApprovalStatus";
import {
  InvoiceFormProps,
  defaultInvoiceLine,
  createBaseValidationSchema,
  createQboLineItemsValidation,
  createQboCustomerValidation,
  createQboVendorValidation,
  createQboAccountValidation,
  pulseAnimationStyles,
  createTaxLineItem,
} from "./invoiceFormUtils";
import {
  checkInvoiceConsistency,
  formatDelta,
} from "./invoiceConsistency";
import InvoiceFieldWarning from "./InvoiceFieldWarning";

const InvoiceQbo: React.FC<InvoiceFormProps> = ({
  invoice,
  getMarginTop,
  tl,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { steps } = useSelector((state: ReduxState) => state.OnboardingSteps);
  const animate = steps.filter(
    (s) => s.name === "DOWNLOAD_DATA" && !s.completed
  );

  const {
    qboAccounts,
    qboVendors,
    qboCustomers,
    qboItemsAll,
    qboAccountCategory,
    qboCustomersLoading,
    qboVendorsLoading,
    qboAccountsLoading,
    qboAccountCategoryLoading,
    qboItemLoading,
    importToQboLoading,
    runInvoiceRulesLoading,
    qboTaxCodes,
    qboTaxCodesLoading,
    duplicateError,
    approvalStatus,
    linkedIntegration,
    linkedIntegrationDetails,
  } = useSelector((state: ReduxState) => ({
    qboAccounts: state.QboAccount.qboAccounts,
    qboAccountsLoading: state.QboAccount.loading,
    qboVendors: state.QboVendor.qboVendors,
    qboVendorsLoading: state.QboVendor.loading,
    qboCustomers: state.QboCustomer.qboCustomers,
    qboCustomersLoading: state.QboCustomer.loading,
    qboItemsAll: state.QboItem.qboItems,
    qboItemLoading: state.QboItem.loading,
    qboAccountCategory: state.QboAccountCategory.qboAccountCategory,
    qboAccountCategoryLoading: state.QboAccountCategory.loading,
    importToQboLoading: state.InvoiceManager.importToQboLoading,
    runInvoiceRulesLoading: state.InvoiceManager.runInvoiceRulesLoading,
    qboTaxCodes: state.QboTaxCode.qboTaxCodes,
    qboTaxCodesLoading: state.QboTaxCode.loading,
    duplicateError: state.InvoiceManager.duplicateError,
    approvalStatus: state.InvoiceApprovalStatus,
    linkedIntegration: state.TagLevelIntegration.linkedIntegration,
    linkedIntegrationDetails:
      state.TagLevelIntegration.linkedIntegrationDetails,
  }));

  const qboItems = (qboItemsAll || []).filter((x) =>
    ["invoice"].includes(invoice.docType)
      ? ["Service", "Inventory"].includes(x.type)
      : ["category"].includes(x.type)
  );

  // Memoize and alphabetize FormikQboSelect options. The parent rebuilding
  // these arrays inline on each render fires QboSelect's defaultOptions
  // useEffect and snaps the user's typed-search results back to the full
  // list. Match the InvoiceXero treatment.
  const collator = useMemo(
    () => new Intl.Collator(undefined, { sensitivity: "base", numeric: true }),
    []
  );
  const qboCustomerOptions = useMemo(
    () =>
      (qboCustomers || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboCustomers, collator]
  );
  const qboVendorOptions = useMemo(
    () =>
      (qboVendors || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboVendors, collator]
  );
  const qboAccountOptions = useMemo(
    () =>
      (qboAccounts || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboAccounts, collator]
  );
  const qboAccountCategoryOptions = useMemo(
    () =>
      (qboAccountCategory || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboAccountCategory, collator]
  );
  const qboItemOptions = useMemo(
    () =>
      (qboItems || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboItems, collator]
  );
  const qboTaxCodeOptions = useMemo(
    () =>
      (qboTaxCodes || [])
        .map((c) => ({ label: c.name, value: c.value }))
        .sort((a, b) => collator.compare(a.label || "", b.label || "")),
    [qboTaxCodes, collator]
  );

  const integrationId = linkedIntegration?.integrationId || invoice.integration;
  const fullPath = location.pathname + location.search;
  const [showHistory, setShowHistory] = useState(false);
  const [billLineItemsMapTo, setBillLineItemsMapTo] = useState<
    "categories" | "products_services"
  >("categories");
  const [loadingBillLineItemsMapTo, setLoadingBillLineItemsMapTo] =
    useState(false);

  const { tagLevels, selectedL1, selectedL2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const currentTagId = (tagLevels?.root as any)?.[selectedL1]?.[selectedL2]
    ?.metadata?.tagId;
  const currentTagLevelId = currentTagId ? parseInt(currentTagId, 10) : null;
  const isQuickBooksConnected =
    linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks;
  const showBillLineItemToggle =
    isQuickBooksConnected && invoice.docType === "bill" && !!currentTagLevelId;

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  // Fetch QuickBooks data when integration is available
  // Don't update invoice fields here - read initial values from backend only
  useEffect(() => {
    if (linkedIntegration && integrationId) {
      dispatch(fetchQboCustomers(integrationId));
      dispatch(fetchQboVendors(integrationId));
      dispatch(fetchQboAccounts(integrationId));
      dispatch(fetchQboAccountCategorys(integrationId));
      dispatch(fetchQboItems(integrationId));
      dispatch(fetchQboTaxCodes(integrationId));
    }
  }, [linkedIntegration, integrationId]);

  useEffect(() => {
    if (invoice?.link) {
      dispatch(setDuplicateError(null));
    }
  }, [invoice?.link]);

  useEffect(() => {
    if (!showBillLineItemToggle || !currentTagLevelId) {
      setBillLineItemsMapTo("categories");
      return;
    }
    let mounted = true;
    setLoadingBillLineItemsMapTo(true);
    getTagLevelSetting(currentTagLevelId, "quickbooks.billLineItemsMapTo")
      .then((rsp: any) => {
        if (!mounted) return;
        const value = rsp?.setting?.value;
        setBillLineItemsMapTo(
          value === "products_services" ? "products_services" : "categories"
        );
      })
      .catch(() => {
        if (!mounted) return;
        setBillLineItemsMapTo("categories");
      })
      .finally(() => {
        if (mounted) setLoadingBillLineItemsMapTo(false);
      });
    return () => {
      mounted = false;
    };
  }, [showBillLineItemToggle, currentTagLevelId]);

  const invoiceManagerSaving = useSelector(
    (state: ReduxState) => state.InvoiceManager.saving
  );

  // Normalize docType when QuickBooks integration is linked.
  // Depend on the primitive `invoice.docType` (not the whole invoice object) so
  // this effect doesn't re-fire on every parent re-render. Also bail while a
  // save is in flight to avoid racing dispatches that loop on stale state.
  useEffect(() => {
    if (invoiceManagerSaving) return;
    if (
      linkedIntegration &&
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks &&
      invoice
    ) {
      // Change payables to bill, receivables to invoice
      if (invoice.docType === "payables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "bill" })
        );
      } else if (invoice.docType === "receivables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "invoice" })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedIntegration, linkedIntegrationDetails, invoice?.docType, invoiceManagerSaving, dispatch]);

  // Handle field changes for QuickBooks-specific fields.
  // When a field has a "name" counterpart (e.g. vendorEdited + vendorEditedName), we update
  // both in one invoice and dispatch once to avoid duplicate API calls.
  const handleFieldChange = ({
    values,
    field,
    value,
    label,
  }: {
    values: Invoice2;
    field: string;
    value: any;
    label?: string;
  }) => {
    let invoiceToUpdate = values;
    const nameFields: Array<{ field: string; value: string }> = [];

    if (field === "vendorEdited") {
      nameFields.push({ field: "vendorEditedName", value: value ? label || getVendorById(value) : "" });
    } else if (field === "vendorAutomated") {
      nameFields.push({ field: "vendorAutomatedName", value: value ? label || getVendorById(value) : "" });
    } else if (field === "vendor") {
      nameFields.push({ field: "vendorName", value: value ? label || getVendorById(value) : "" });
    } else if (field === "customerEdited") {
      nameFields.push({ field: "customerEditedName", value: value ? label || getCustomerById(value) : "" });
    } else if (field === "customerAutomated") {
      nameFields.push({ field: "customerAutomatedName", value: value ? label || getCustomerById(value) : "" });
    } else if (field === "customer") {
      nameFields.push({ field: "customerName", value: value ? label || getCustomerById(value) : "" });
    } else if (field === "accountEdited") {
      nameFields.push({ field: "accountEditedName", value: value ? getAccountById(value) : "" });
    } else if (field === "accountAutomated") {
      nameFields.push({ field: "accountAutomatedName", value: value ? getAccountById(value) : "" });
    } else if (field === "account") {
      nameFields.push({ field: "accountName", value: value ? getAccountById(value) : "" });
    }

    if (nameFields.length > 0) {
      invoiceToUpdate = { ...values, [field]: value } as Invoice2;
      for (const { field: nameField, value: nameValue } of nameFields) {
        (invoiceToUpdate as any)[nameField] = nameValue;
      }
    }

    dispatch(updateInvoiceField({ invoice: invoiceToUpdate, field, value }));
  };

  // Handle line item changes for QuickBooks-specific fields
  const handleLineItemChange = ({
    values,
    lineIndex,
    field,
    value,
    label,
  }: {
    values: Invoice2;
    lineIndex: number;
    field: string;
    value: any;
    label?: string;
  }) => {
    const updatedValues = { ...values };
    const updatedLines = [...updatedValues.lines];
    const updatedLine = { ...updatedLines[lineIndex] };

    // Category name handling
    if (field === "categoryEdited") {
      updatedLine.categoryEditedName = value
        ? label || getCategoryById(value)
        : "";
    } else if (field === "categoryAutomated") {
      updatedLine.categoryAutomatedName = value
        ? label || getCategoryById(value)
        : "";
    } else if (field === "category") {
      updatedLine.categoryName = value ? label || getCategoryById(value) : "";
    }

    // Service name handling
    else if (field === "serviceEdited") {
      updatedLine.serviceEditedName = value
        ? label || getServiceById(value)
        : "";
    } else if (field === "serviceAutomated") {
      updatedLine.serviceAutomatedName = value
        ? label || getServiceById(value)
        : "";
    } else if (field === "service") {
      updatedLine.serviceName = value ? label || getServiceById(value) : "";
    }

    // Tax code name handling
    else if (field === "taxEdited") {
      updatedLine.taxEditedName = value ? label || getTaxCodeById(value) : "";
    } else if (field === "taxAutomated") {
      updatedLine.taxAutomatedName = value
        ? label || getTaxCodeById(value)
        : "";
    } else if (field === "tax") {
      updatedLine.taxName = value ? label || getTaxCodeById(value) : "";
    }

    // Set the original field value
    updatedLine[field] = value;

    updatedLines[lineIndex] = updatedLine;
    updatedValues.lines = updatedLines;

    dispatch(
      updateInvoiceLineItem({
        invoice: updatedValues,
        lineIndex,
        field,
        value: value || "",
      })
    );
  };

  // Fetch approval status
  useEffect(() => {
    if (invoice?.id && tl.id) {
      dispatch(fetchApprovalStatus(tl.id));
    }

    return () => {
      dispatch(resetState());
    };
  }, [JSON.stringify(invoice), tl.id, dispatch]);

  const handleApprove = async (comment = "") => {
    try {
      await dispatch(approveInvoice(Number(tl.id), comment));
    } catch (error) {
      console.error("Error approving invoice:", error);
    }
  };

  const handleReject = async (comment = "") => {
    try {
      await dispatch(rejectInvoice(Number(tl.id), comment));
    } catch (error) {
      console.error("Error rejecting invoice:", error);
    }
  };

  if (!invoice) return null;

  // Load options functions for async search (use integrationId so linked folder/job integration is used when invoice.integration is not set)
  const loadCustomerOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboCustomers(
        Number(integrationId),
        inputValue
      );
      return results.map((c: any) => ({
        label: c.DisplayName,
        value: c.Id,
      }));
    } catch (error) {
      console.error("Error searching customers:", error);
      return [];
    }
  };

  const loadVendorOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboVendors(
        Number(integrationId),
        inputValue
      );
      return results.map((v: any) => ({
        label: v.DisplayName,
        value: v.Id,
      }));
    } catch (error) {
      console.error("Error searching vendors:", error);
      return [];
    }
  };

  const loadAccountOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboAccounts(
        Number(integrationId),
        true,
        inputValue
      );
      return results.map((a: any) => ({
        label: a.Name,
        value: a.Id,
      }));
    } catch (error) {
      console.error("Error searching accounts:", error);
      return [];
    }
  };

  const loadCategoryOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboAccounts(
        Number(integrationId),
        false,
        inputValue
      );
      return results.map((c: any) => ({
        label: c.Name,
        value: c.Id,
      }));
    } catch (error) {
      console.error("Error searching categories:", error);
      return [];
    }
  };

  const loadServiceOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboItems(
        Number(integrationId),
        inputValue
      );
      return results.map((i: any) => ({
        label: i.Name,
        value: i.Id,
      }));
    } catch (error) {
      console.error("Error searching items:", error);
      return [];
    }
  };

  const loadTaxCodeOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchQboTaxCodes(
        integrationId.toString(),
        inputValue
      );
      return results.map((t: any) => ({
        label: t.Name,
        value: t.Id,
      }));
    } catch (error) {
      console.error("Error searching tax codes:", error);
      return [];
    }
  };

  // Helper functions for QuickBooks data lookups
  const getCategoryById = (id: any) => {
    const category = qboAccountCategory.find((x) => x.value === id);
    return category ? category.name : "";
  };

  const getServiceById = (id: any) => {
    const service = qboItems.find((x) => x.value === id);
    return service ? service.name : "";
  };

  const getTaxCodeById = (id: any) => {
    const taxCode = qboTaxCodes.find((x) => x.value === id);
    return taxCode ? taxCode.name : "";
  };

  const getCustomerById = (id: any) => {
    const customer = qboCustomers.find((x) => x.value === id);
    return customer ? customer.name : "";
  };

  const getVendorById = (id: any) => {
    const vendor = qboVendors.find((x) => x.value === id);
    return vendor ? vendor.name : "";
  };

  const getAccountById = (id: any) => {
    const account = qboAccounts.find((x) => x.value === id);
    return account ? account.name : "";
  };

  const ctaLabel = `Import ${
    invoice?.docType === "invoice"
      ? "invoice"
      : invoice?.docType === "bill"
      ? "bill"
      : "expense"
  } into QuickBooks`;

  // Validation schema for QuickBooks
  const validationSchema = Yup.object().shape({
    ...createBaseValidationSchema(invoice.docType),
    lines: createQboLineItemsValidation(invoice.docType, billLineItemsMapTo),
    ...(invoice.docType === "invoice" && {
      customer: createQboCustomerValidation(),
    }),
    ...(["bill", "expense"].includes(invoice.docType) && {
      vendor: createQboVendorValidation(),
    }),
    ...(invoice.docType === "expense" && {
      account: createQboAccountValidation(),
    }),
  });

  // Initial values with QuickBooks-specific fields
  const initialValues = {
    ...invoice,
    // QuickBooks customer fields
    customer: invoice.customer || "",
    customerName: invoice.customerName || "",
    customerEdited: invoice.customerEdited || "",
    customerEditedName: invoice.customerEditedName || "",
    customerAutomated: invoice.customerAutomated || "",
    customerAutomatedName: invoice.customerAutomatedName || "",
    // QuickBooks vendor fields
    vendor: invoice.vendor || "",
    vendorName: invoice.vendorName || "",
    vendorEdited: invoice.vendorEdited || "",
    vendorEditedName: invoice.vendorEditedName || "",
    vendorAutomated: invoice.vendorAutomated || "",
    vendorAutomatedName: invoice.vendorAutomatedName || "",
    // QuickBooks account fields
    account: invoice.account || "",
    accountName: invoice.accountName || "",
    accountEdited: invoice.accountEdited || "",
    accountEditedName: invoice.accountEditedName || "",
    accountAutomated: invoice.accountAutomated || "",
    accountAutomatedName: invoice.accountAutomatedName || "",
    // Line items with QuickBooks-specific fields
    lines: invoice.lines.map((l: LineItem2) => ({
      ...l,
      categoryEdited: l?.categoryEdited || "",
      serviceEdited: l?.serviceEdited || "",
      categoryAutomated: l?.categoryAutomated || "",
      serviceAutomated: l?.serviceAutomated || "",
    })),
  };


  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        context={{ docType: invoice.docType }}
        onSubmit={(values: Invoice2) => {
          dispatch(handleImportToQbo(values));
        }}
      >
        {({ values, setFieldValue, submitForm, errors, validateForm }) => {
          const integrationWithError =
            linkedIntegrationDetails &&
            (linkedIntegrationDetails.errorMessage ||
              linkedIntegrationDetails.errorCode !== null);

          return (
            <>
              {/* Duplicate error display */}
              <Row>
                <Col className="d-flex justify-content-end align-items-center">
                  <div>
                    {duplicateError && (
                      <Alert
                        variant="danger"
                        className="mb-0 mr-3"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <span>
                          Duplicate {duplicateError.docType} found. View in
                          QuickBooks{" "}
                          <a
                            href={duplicateError.qboUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            here
                          </a>
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          className="ml-3"
                          onClick={() =>
                            dispatch(handleImportToQbo(values, true))
                          }
                        >
                          Force Import
                        </Button>
                      </Alert>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Customer field for invoices */}
              <Row>
                {["invoice"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Customer</Form.Label>
                      </strong>
                      <Field
                        name="customer"
                        placeholder="Search or create a new customer"
                        value={
                          invoice.customerEdited ||
                          invoice.customerAutomated ||
                          invoice.customer
                            ? {
                                label: getCustomerById(
                                  invoice.customerEdited ||
                                    invoice.customerAutomated ||
                                    invoice.customer
                                ),
                                value:
                                  invoice.customerEdited ||
                                  invoice.customerAutomated ||
                                  invoice.customer,
                              }
                            : null
                        }
                        component={FormikQboSelect}
                        options={qboCustomerOptions}
                        createFn={async (value: string) => {
                          try {
                            return await dispatch(
                              createAndAddQboCustomer(integrationId, value)
                            );
                          } catch (err) {
                            dispatch(
                              createAlert({
                                message:
                                  (err as any)?.message ||
                                  "Failed to create customer. Please try again.",
                                type: "error",
                                timeout: 5000,
                              })
                            );
                            throw err;
                          }
                        }}
                        type="customer"
                        onChangeFn={(value: any, label?: string) => {
                          handleFieldChange({
                            values,
                            field: "customerEdited",
                            value,
                            label,
                          });
                        }}
                        loadOptions={loadCustomerOptions}
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Vendor field for bills/expenses */}
                {["bill", "expense"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Vendor</Form.Label>
                      </strong>
                      <Field
                        name="vendor"
                        placeholder="Search or create a new vendor"
                        value={
                          invoice.vendorEdited ||
                          invoice.vendorAutomated ||
                          invoice.vendor
                            ? {
                                label: getVendorById(
                                  invoice.vendorEdited ||
                                    invoice.vendorAutomated ||
                                    invoice.vendor
                                ),
                                value:
                                  invoice.vendorEdited ||
                                  invoice.vendorAutomated ||
                                  invoice.vendor,
                              }
                            : null
                        }
                        component={FormikQboSelect}
                        options={qboVendorOptions}
                        createFn={async (value: string) => {
                          try {
                            return await dispatch(
                              createAndAddQboVendor(integrationId, value)
                            );
                          } catch (err) {
                            dispatch(
                              createAlert({
                                message:
                                  (err as any)?.message ||
                                  "Failed to create vendor. Please try again.",
                                type: "error",
                                timeout: 5000,
                              })
                            );
                            throw err;
                          }
                        }}
                        type="vendor"
                        onChangeFn={(value: any, label?: string) => {
                          handleFieldChange({
                            values,
                            field: "vendorEdited",
                            value,
                            label,
                          });
                        }}
                        loadOptions={loadVendorOptions}
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Account field for expenses */}
                {["expense"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Account</Form.Label>
                      </strong>
                      <Field
                        name="account"
                        placeholder="Search or create a new account"
                        value={
                          invoice.accountEdited ||
                          invoice.accountAutomated ||
                          invoice.account
                            ? {
                                label: getAccountById(
                                  invoice.accountEdited ||
                                    invoice.accountAutomated ||
                                    invoice.account
                                ),
                                value:
                                  invoice.accountEdited ||
                                  invoice.accountAutomated ||
                                  invoice.account,
                              }
                            : null
                        }
                        component={FormikQboSelect}
                        options={qboAccountOptions}
                        createFn={async (value: string) => {
                          try {
                            return await dispatch(
                              createAndAddQboAccount(integrationId, value)
                            );
                          } catch (err) {
                            dispatch(
                              createAlert({
                                message:
                                  (err as any)?.message ||
                                  "Failed to create account. Please try again.",
                                type: "error",
                                timeout: 5000,
                              })
                            );
                            throw err;
                          }
                        }}
                        type="account"
                        onChangeFn={(value: any, label?: string) => {
                          handleFieldChange({
                            values,
                            field: "accountEdited",
                            value,
                            label,
                          });
                        }}
                        loadOptions={loadAccountOptions}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {/* ID, Date, Due Date row */}
              <Row>
                <Col>
                  <FormGroup>
                    <strong>
                      <Form.Label>Id #</Form.Label>
                    </strong>
                    <Field
                      placeholder=""
                      name="id"
                      type="text"
                      component={formikInput}
                      onBlur={(event: any) =>
                        handleFieldChange({
                          values,
                          field: "id",
                          value: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <strong>
                      <Form.Label>Date</Form.Label>
                    </strong>
                    <Field
                      data-cy={`${invoice.docType}-date`}
                      placeholder=""
                      name="date"
                      type="text"
                      component={formikInput}
                      onBlur={(event: any) =>
                        handleFieldChange({
                          values,
                          field: "date",
                          value: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                {["bill", "invoice"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Due date</Form.Label>
                      </strong>
                      <Field
                        placeholder=""
                        name="dueDate"
                        type="text"
                        component={formikInput}
                        onBlur={(event: any) =>
                          handleFieldChange({
                            values,
                            field: "dueDate",
                            value: event.target.value,
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {/* Subtotal / Tax / Total row */}
              {(() => {
                const consistency = checkInvoiceConsistency(values);
                const headerMsg = consistency.headerMismatch
                  ? `Subtotal + Tax (${(
                      consistency.subTotal + consistency.tax
                    ).toFixed(2)}) ≠ Total (${consistency.total.toFixed(
                      2
                    )}). Δ ${formatDelta(consistency.headerDelta)}.`
                  : "";
                return (
                  <Row>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Subtotal
                            <InvoiceFieldWarning
                              id="qbo-subtotal"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <Field
                          placeholder=""
                          name="subTotal"
                          type="text"
                          component={formikInput}
                          onBlur={(event: any) =>
                            handleFieldChange({
                              values,
                              field: "subTotal",
                              value: event.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Tax
                            <InvoiceFieldWarning
                              id="qbo-tax"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <Field
                              placeholder=""
                              name="tax"
                              type="text"
                              component={formikInput}
                              onBlur={(event: any) =>
                                handleFieldChange({
                                  values,
                                  field: "tax",
                                  value: event.target.value,
                                })
                              }
                            />
                          </div>
                          {values.tax &&
                            values.tax !== "" &&
                            Number(values.tax) !== 0 && (
                              <Button
                                variant="link"
                                size="sm"
                                className="ml-2"
                                onClick={() => {
                                  const taxValue = values.tax;
                                  const newLineItem = createTaxLineItem(taxValue);
                                  const updatedInvoice = {
                                    ...values,
                                    lines: [...values.lines, newLineItem],
                                    tax: "",
                                  };

                                  dispatch(
                                    updateInvoiceField({
                                      invoice: updatedInvoice,
                                      field: "lines",
                                      value: updatedInvoice.lines,
                                    })
                                  );
                                  dispatch(
                                    updateInvoiceField({
                                      invoice: updatedInvoice,
                                      field: "tax",
                                      value: "",
                                    })
                                  );
                                  setFieldValue("lines", updatedInvoice.lines);
                                  setFieldValue("tax", "");
                                }}
                                title="Add tax as line item"
                              >
                                <FontAwesomeIcon icon="plus" className="mr-1" />
                                Add Tax as Line Item
                              </Button>
                            )}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Total
                            <InvoiceFieldWarning
                              id="qbo-total"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <Field
                          placeholder=""
                          name="total"
                          type="text"
                          component={formikInput}
                          onBlur={(event: any) =>
                            handleFieldChange({
                              values,
                              field: "total",
                              value: event.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                );
              })()}

              {/* Line Items */}
              <Row className="ml-0">
                <div className="w-100 mb-2 d-flex align-items-center justify-content-between">
                  <strong>Line Items</strong>
                  {showBillLineItemToggle && (
                    <Form.Group className="mb-0">
                      <div
                        className="d-flex align-items-center flex-wrap"
                        style={{ gap: "0.75rem" }}
                      >
                        <small className="text-muted">Map to</small>
                        <Form.Check
                          inline
                          type="radio"
                          id="bill-map-to-categories"
                          label="Category"
                          name="bill-line-items-map-to"
                          checked={billLineItemsMapTo === "categories"}
                          disabled={loadingBillLineItemsMapTo}
                          onChange={async () => {
                            if (!currentTagLevelId) return;
                            const previousValue = billLineItemsMapTo;
                            const nextValue = "categories" as const;
                            setBillLineItemsMapTo(nextValue);
                            try {
                              await upsertTagLevelSetting(
                                currentTagLevelId,
                                "quickbooks.billLineItemsMapTo",
                                nextValue
                              );
                            } catch (err) {
                              setBillLineItemsMapTo(previousValue);
                              dispatch(
                                createAlert({
                                  message:
                                    "Failed to save line item mapping setting. Please try again.",
                                  type: "error",
                                  timeout: 5000,
                                })
                              );
                            }
                          }}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          id="bill-map-to-products-services"
                          label="Product/Service"
                          name="bill-line-items-map-to"
                          checked={billLineItemsMapTo === "products_services"}
                          disabled={loadingBillLineItemsMapTo}
                          onChange={async () => {
                            if (!currentTagLevelId) return;
                            const previousValue = billLineItemsMapTo;
                            const nextValue = "products_services" as const;
                            setBillLineItemsMapTo(nextValue);
                            try {
                              await upsertTagLevelSetting(
                                currentTagLevelId,
                                "quickbooks.billLineItemsMapTo",
                                nextValue
                              );
                            } catch (err) {
                              setBillLineItemsMapTo(previousValue);
                              dispatch(
                                createAlert({
                                  message:
                                    "Failed to save line item mapping setting. Please try again.",
                                  type: "error",
                                  timeout: 5000,
                                })
                              );
                            }
                          }}
                        />
                      </div>
                    </Form.Group>
                  )}
                </div>
                <FieldArray name="lines" key={invoice?.integration}>
                  {({ remove, push }) => (
                    <div
                      className="w-100"
                      style={{
                        maxHeight: "120vh",
                        overflowY: "scroll",
                      }}
                    >
                      {values.lines.length > 0 &&
                        values.lines.map((lineItem: any, index: number) => (
                          <div className="row my-2" key={`line-${index}`}>
                            {/* Category/Service column */}
                            <div className="col-3 pr-0">
                              {index === 0 && (
                                <strong>
                                  {(["bill", "expense"].includes(
                                    values.docType
                                  ) &&
                                    !(
                                      values.docType === "bill" &&
                                      billLineItemsMapTo === "products_services"
                                    )) && (
                                    <label
                                      htmlFor={`lines.${index}.categoryEdited`}
                                    >
                                      Category
                                    </label>
                                  )}
                                  {(["invoice"].includes(values.docType) ||
                                    (values.docType === "bill" &&
                                      billLineItemsMapTo ===
                                        "products_services")) && (
                                    <label
                                      htmlFor={`lines.${index}.serviceEdited`}
                                    >
                                      Product/Service
                                    </label>
                                  )}
                                </strong>
                              )}

                              {/* Category for bills/expenses */}
                              {(["bill", "expense"].includes(
                                values.docType
                              ) &&
                                !(
                                  values.docType === "bill" &&
                                  billLineItemsMapTo === "products_services"
                                )) && (
                                <div
                                  style={{
                                    position: "relative",
                                    marginBottom: "1rem",
                                  }}
                                >
                                  <CategorizationBadge
                                    primaryCondition={lineItem.categoryEdited}
                                    secondaryCondition={
                                      lineItem.categoryAutomated
                                    }
                                  />
                                  <Field
                                    key={`lines.${index}.category-${lineItem.categoryEdited}-${lineItem.categoryAutomated}`}
                                    name={`lines.${index}.categoryEdited`}
                                    placeholder="Category"
                                    value={{
                                      label: getCategoryById(
                                        lineItem?.categoryEdited ||
                                          lineItem?.categoryAutomated ||
                                          ""
                                      ),
                                      value:
                                        lineItem?.categoryEdited ||
                                        lineItem?.categoryAutomated ||
                                        "",
                                    }}
                                    component={FormikQboSelect}
                                    options={qboAccountCategoryOptions}
                                    createFn={async (value: string) => {
                                      try {
                                        return await dispatch(
                                          createAndAddQboAccountCategory(
                                            invoice.integration,
                                            value
                                          )
                                        );
                                      } catch (err) {
                                        dispatch(
                                          createAlert({
                                            message:
                                              (err as any)?.message ||
                                              "Failed to create category. Please try again.",
                                            type: "error",
                                            timeout: 5000,
                                          })
                                        );
                                        throw err;
                                      }
                                    }}
                                    onChangeFn={(
                                      value: any,
                                      label?: string
                                    ) => {
                                      handleLineItemChange({
                                        values,
                                        lineIndex: index,
                                        field: "categoryEdited",
                                        value: !value ? "" : value,
                                        label,
                                      });
                                    }}
                                    type="category"
                                    style={{
                                      marginTop:
                                        index === 0 &&
                                        (lineItem.categoryEdited ||
                                          lineItem.categoryAutomated)
                                          ? "1rem"
                                          : "0",
                                    }}
                                    validate={() => {
                                      const hasCategory =
                                        lineItem.category ||
                                        lineItem.categoryEdited ||
                                        lineItem.categoryAutomated;
                                      return !hasCategory
                                        ? "Category is required"
                                        : undefined;
                                    }}
                                    loadOptions={loadCategoryOptions}
                                  />
                                </div>
                              )}

                              {/* Service for invoices */}
                              {(["invoice"].includes(values.docType) ||
                                (values.docType === "bill" &&
                                  billLineItemsMapTo ===
                                    "products_services")) && (
                                <div
                                  style={{
                                    position: "relative",
                                    marginBottom: "1rem",
                                  }}
                                >
                                  <CategorizationBadge
                                    primaryCondition={lineItem.serviceEdited}
                                    secondaryCondition={
                                      lineItem.serviceAutomated
                                    }
                                  />
                                  <Field
                                    key={`lines.${index}.service-${lineItem.serviceEdited}-${lineItem.serviceAutomated}`}
                                    name={`lines.${index}.serviceEdited`}
                                    placeholder="Product/Service"
                                    value={{
                                      label:
                                        lineItem?.serviceEditedName ||
                                        getServiceById(
                                          lineItem?.serviceEdited ||
                                            lineItem?.serviceAutomated ||
                                            lineItem?.service ||
                                            ""
                                        ),
                                      value:
                                        lineItem?.serviceEdited ||
                                        lineItem?.serviceAutomated ||
                                        lineItem?.service ||
                                        "",
                                    }}
                                    className="pr-0"
                                    component={FormikQboSelect}
                                    options={qboItemOptions}
                                    onChangeFn={(
                                      value: any,
                                      label?: string
                                    ) => {
                                      handleLineItemChange({
                                        values,
                                        lineIndex: index,
                                        field: "serviceEdited",
                                        value: value || "",
                                        label,
                                      });
                                    }}
                                    type="service"
                                    style={{
                                      marginTop:
                                        index === 0 &&
                                        (lineItem.serviceEdited ||
                                          lineItem.serviceAutomated)
                                          ? "1rem"
                                          : "0",
                                    }}
                                    validate={() => {
                                      const hasService =
                                        lineItem.service ||
                                        lineItem.serviceEdited ||
                                        lineItem.serviceAutomated;
                                      return !hasService
                                        ? "Product/service is required"
                                        : undefined;
                                    }}
                                    loadOptions={loadServiceOptions}
                                  />
                                </div>
                              )}

                            </div>

                            {/* Description column */}
                            <div className="col-3 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.item`}>
                                    Description
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.item`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "item",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Product Code column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    htmlFor={`lines.${index}.productCode`}
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                  >
                                    Product Code
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.productCode`}
                                value={
                                  lineItem.productCode ||
                                  (lineItem.extraFields &&
                                    lineItem.extraFields.PRODUCT_CODE) ||
                                  ""
                                }
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "productCode",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Quantity column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                    htmlFor={`lines.${index}.quantity`}
                                  >
                                    Quantity
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.quantity`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "quantity",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Unit Price column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    htmlFor={`lines.${index}.unitPrice`}
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                  >
                                    Unit Price
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.unitPrice`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "unitPrice",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Price column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.price`}>
                                    Price
                                  </label>
                                </strong>
                              )}
                              {(() => {
                                const lc = checkInvoiceConsistency(values);
                                const msg = lc.linesMismatch
                                  ? `Line items sum (${lc.linesSum.toFixed(
                                      2
                                    )}) ≠ Subtotal (${lc.subTotal.toFixed(
                                      2
                                    )}). Δ ${formatDelta(lc.linesDelta)}.`
                                  : "";
                                return (
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                      <Field
                                        name={`lines.${index}.price`}
                                        placeholder=""
                                        type="text"
                                        component={formikArrayInput}
                                        onBlur={(event: any) =>
                                          handleLineItemChange({
                                            values,
                                            lineIndex: index,
                                            field: "price",
                                            value: event.target.value,
                                          })
                                        }
                                        style={{
                                          marginTop: getMarginTop(
                                            index,
                                            lineItem,
                                            "1rem",
                                            "0"
                                          ),
                                        }}
                                      />
                                    </div>
                                    <InvoiceFieldWarning
                                      id={`qbo-line-price-${index}`}
                                      show={lc.linesMismatch}
                                      message={msg}
                                    />
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Tax column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.tax`}>
                                    Tax
                                  </label>
                                </strong>
                              )}
                              <div
                                style={{
                                  position: "relative",
                                  marginBottom: "1rem",
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              >
                                <CategorizationBadge
                                  primaryCondition={lineItem.taxEdited}
                                  secondaryCondition={lineItem.taxAutomated}
                                />
                                <Field
                                  key={`lines.${index}.tax-${lineItem.taxEdited}-${lineItem.taxAutomated}`}
                                  name={`lines.${index}.taxEdited`}
                                  placeholder="Tax"
                                  value={{
                                    label: getTaxCodeById(
                                      lineItem?.taxEdited ||
                                        lineItem?.taxAutomated ||
                                        ""
                                    ),
                                    value:
                                      lineItem?.taxEdited ||
                                      lineItem?.taxAutomated ||
                                      "",
                                  }}
                                  className="pr-0"
                                  component={FormikQboSelect}
                                  options={qboTaxCodeOptions}
                                  onChangeFn={(value: any, label?: string) => {
                                    handleLineItemChange({
                                      values,
                                      lineIndex: index,
                                      field: "taxEdited",
                                      value: value || "",
                                      label,
                                    });
                                  }}
                                  type="tax"
                                  loadOptions={loadTaxCodeOptions}
                                />
                              </div>
                            </div>

                            {/* Delete button */}
                            <TrackedButtonWithTooltip
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginRight: "0.5rem",
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1.5rem",
                                  "-1rem"
                                ),
                              }}
                              id={index.toString()}
                              placement="top"
                              variant="link"
                              renderTooltip={() =>
                                values.lines.length === 1 ? (
                                  <>Cannot delete last line item</>
                                ) : (
                                  <>Delete Line Item</>
                                )
                              }
                              action="removeItem"
                              onClick={() =>
                                values.lines.length > 1 && remove(index)
                              }
                              disabled={values.lines.length === 1}
                            >
                              <FontAwesomeIcon icon="trash" />
                            </TrackedButtonWithTooltip>
                          </div>
                        ))}

                      <Button
                        variant="link"
                        className="secondary"
                        onClick={() => push(defaultInvoiceLine)}
                      >
                        <FontAwesomeIcon icon="plus" />
                        <span className="ml-1">Add Line Item</span>
                      </Button>
                      <div className="p-5"></div>
                    </div>
                  )}
                </FieldArray>
              </Row>

              {/* Bottom duplicate error */}
              <Row className="mt-2 text-right">
                <Col
                  md={12}
                  className="d-flex justify-content-end align-items-center"
                >
                  {duplicateError && (
                    <Alert
                      variant="danger"
                      className="mb-0 mr-3"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <span>
                        Duplicate {duplicateError.docType} found. View in
                        QuickBooks{" "}
                        <a
                          href={duplicateError.qboUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          here
                        </a>
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        className="ml-3"
                        onClick={() =>
                          dispatch(handleImportToQbo(values, true))
                        }
                      >
                        Force Import
                      </Button>
                    </Alert>
                  )}
                </Col>
              </Row>
            </>
          );
        }}
      </Formik>

      <TagRegexesInvoice
        categories={
          ["bill", "expense"].includes(invoice.docType)
            ? qboAccountCategory
            : qboItems
        }
        docType={invoice.docType}
        integrationId={integrationId ? Number(integrationId) : null}
      />

      <ApprovalHistory
        show={showHistory}
        onHide={() => setShowHistory(false)}
        transactionLineId={tl.id ? Number(tl.id) : 0}
      />
    </>
  );
};

export default InvoiceQbo;
